export interface SpeechError {
  type: string;
  message: string;
}

export interface BrowserSupport {
  supported: boolean;
  browser?: string;
  notes?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  addEventListener(event: string, handler: (...args: unknown[]) => void): void;
  removeEventListener(event: string, handler: (...args: unknown[]) => void): void;
}

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export interface TranscriptResult {
  words: string[];
  confidence: number | null;
  alternativeCount: number;
}

type TranscriptCallback = (words: string[], meta?: TranscriptResult) => void;
type ErrorCallback = (error: SpeechError) => void;
type EndCallback = () => void;

export class SpeechRecognitionAdapter {
  private recognition: SpeechRecognitionLike | null = null;
  private _isListening = false;
  private _intentionallyStopped = false;
  private _continuous: boolean;
  private _isSupported: boolean;

  private transcriptCallbacks = new Set<TranscriptCallback>();
  private interimCallbacks = new Set<TranscriptCallback>();
  private errorCallbacks = new Set<ErrorCallback>();
  private endCallbacks = new Set<EndCallback>();

  private lastProcessedIndex = -1;

  private handleResult: (event: unknown) => void;
  private handleError: (event: unknown) => void;
  private handleEnd: () => void;

  constructor(options?: {
    lang?: string;
    continuous?: boolean;
    SpeechRecognition?: SpeechRecognitionConstructor;
  }) {
    this._continuous = options?.continuous ?? false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = typeof window !== 'undefined' ? (window as any) : undefined;
    const Ctor =
      options?.SpeechRecognition ??
      /* c8 ignore next */
      ((win?.SpeechRecognition ?? win?.webkitSpeechRecognition) as
        | SpeechRecognitionConstructor
        | undefined);

    this._isSupported = !!Ctor;

    if (Ctor) {
      this.recognition = new Ctor();
      this.recognition.lang = options?.lang ?? 'en-US';
      this.recognition.continuous = this._continuous;
      this.recognition.interimResults = true;

      this.handleResult = this.onResult.bind(this);
      this.handleError = this.onErrorEvent.bind(this);
      this.handleEnd = this.onEndEvent.bind(this);

      this.recognition.addEventListener('result', this.handleResult);
      this.recognition.addEventListener('error', this.handleError);
      this.recognition.addEventListener('end', this.handleEnd);
    } else {
      this.handleResult = () => {};
      this.handleError = () => {};
      this.handleEnd = () => {};
    }
  }

  start(): void {
    if (this._isListening || !this.recognition) return;
    this._isListening = true;
    this._intentionallyStopped = false;
    this.lastProcessedIndex = -1;
    this.recognition.start();
  }

  stop(): void {
    if (!this._isListening || !this.recognition) return;
    this._intentionallyStopped = true;
    this._isListening = false;
    this.recognition.stop();
  }

  onTranscript(cb: TranscriptCallback): () => void {
    this.transcriptCallbacks.add(cb);
    return () => this.transcriptCallbacks.delete(cb);
  }

  onInterim(cb: TranscriptCallback): () => void {
    this.interimCallbacks.add(cb);
    return () => this.interimCallbacks.delete(cb);
  }

  onError(cb: ErrorCallback): () => void {
    this.errorCallbacks.add(cb);
    return () => this.errorCallbacks.delete(cb);
  }

  onEnd(cb: EndCallback): () => void {
    this.endCallbacks.add(cb);
    return () => this.endCallbacks.delete(cb);
  }

  get isSupported(): boolean {
    return this._isSupported;
  }

  get isListening(): boolean {
    return this._isListening;
  }

  static checkBrowserSupport(Ctor?: SpeechRecognitionConstructor): BrowserSupport {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = typeof window !== 'undefined' ? (window as any) : undefined;
    const available =
      Ctor ??
      /* c8 ignore next */
      ((win?.SpeechRecognition ?? win?.webkitSpeechRecognition) as
        | SpeechRecognitionConstructor
        | undefined);

    if (!available) {
      return { supported: false };
    }
    return { supported: true };
  }

  private onResult(event: unknown): void {
    const e = event as {
      resultIndex: number;
      results: {
        0: { transcript: string; confidence?: number };
        isFinal: boolean;
        length: number;
      }[];
    };
    const results = e.results;

    // Only process results from resultIndex onward (new/changed results).
    // Skip already-finalized results we've seen before.
    const startIdx = Math.max(e.resultIndex ?? 0, this.lastProcessedIndex + 1);

    for (let i = startIdx; i < results.length; i++) {
      const result = results[i];
      const transcript = result[0].transcript.trim();
      if (!transcript) continue;
      const words = transcript.split(/\s+/);

      const meta: TranscriptResult = {
        words,
        confidence: result[0].confidence ?? null,
        alternativeCount: result.length,
      };

      if (result.isFinal) {
        this.lastProcessedIndex = i;
        for (const cb of this.transcriptCallbacks) cb(words, meta);
      } else {
        for (const cb of this.interimCallbacks) cb(words, meta);
      }
    }
  }

  private onErrorEvent(event: unknown): void {
    const e = event as { error: string; message?: string };
    const error: SpeechError = {
      type: e.error,
      message: e.message ?? '',
    };
    for (const cb of this.errorCallbacks) cb(error);
  }

  private onEndEvent(): void {
    for (const cb of this.endCallbacks) cb();

    // Auto-restart if continuous and not explicitly stopped
    if (this._continuous && !this._intentionallyStopped && this.recognition) {
      this.lastProcessedIndex = -1;
      this.recognition.start();
    } else {
      this._isListening = false;
    }
  }
}
